import { Injectable } from '@angular/core';
import { AngularFirestore, Query } from '@angular/fire/firestore';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { ConsumerPoints } from '@common/models/consumer-points.model';
import { HistoryPointsCalculation } from '@common/models/history-points-calculation.model';
import { HistoryPointsPerMonth } from '@common/models/history-points-per-month.model';
import { HistoryPoints } from '@common/models/history-points.model';
import { ItemsExchanged } from '@common/models/items-exchanged.model';
import { BehaviorSubject, from, Observable, of, zip } from 'rxjs';
import { catchError, defaultIfEmpty, map, switchMap, take } from 'rxjs/operators';
import { ProductsService } from '../products/products.service';
import {
  addMonths,
  closestTo,
  eachMonthOfInterval,
  endOfMonth,
  isSameMonth,
  startOfMonth,
  set,
  getDate,
  startOfYear,
  startOfDay,
  endOfDay, subMonths } from 'date-fns';
import { HistoryPointListPerDate } from '@common/models/history-points-list-per-date.model';
import { PointsPromo } from '@common/models/points-promo.model';
import { BrandService } from '../brand/brand.service';
import { ProductPoints } from '@common/models/product-points.model';
import { ExchangedItemsStatusCode } from '@common/enums/exchanged-items-status-code.enum';
import { RedeemableArticles } from '@common/interfaces/redeemable-articles.interface';
import { KpisAnnual } from '@common/models/kpis-annual.modal';
import { SaleItem } from '@common/interfaces/sale-items.interface';
import { UserProviderService } from '../providers/user-provider.service';
import { BrandsPoints } from '@common/models/brandsPoints.model';
import { Sale } from '@common/models/sale.model';
import { ConsumerBrand } from '@common/models/consumer-brand.model';
import { ProductTypes } from '@common/enums/product-types.enum';
import { Consumer } from '@common/models/consumer.model';
import { Brand } from '@common/interfaces/brand.interface';
import { SaleService } from '../sale/sale.service';
import { ConsumerService } from './consumer.service';
import { UserModel } from '@common/models/user/user.model';
import { CenterService } from '../center/center.service';
import { StandService } from '../stand/stand.service';

@Injectable({
  providedIn: 'root'
})
export class ConsumerFidelityService {
  public static readonly collectionPoints = 'PUNTOS_CONSUMIDORAS';
  public static readonly collectionPointsRedeemed = 'ARTICULOS_CANJE';
  public static readonly collectionPointsPendingExpiration = 'HISTORICO_PUNTOS_CALCULO';
  public static readonly collectionPointsHistory = 'HISTORICO_PUNTOS';
  public static readonly collectionPointsPromo = 'PROMOS_PUNTOS';
  public static readonly collectionProductSale = 'ARTICULOS_VENTA';
  public static readonly collectionTMPConsuTAUG = 'TMP_ConsuTAUG';
  public static readonly collectionSales = 'VENTAS';
  public static readonly collectionConsumerBrand = 'CONSUMIDORA_MARCA';
  public static readonly idPromotionExpired = -10;
  public static readonly howManyMonthsOfSalesForFidelity = 12;

  private isRecyclingTableUpdated = new BehaviorSubject<boolean>(false);
  currentRecyclingTableState = this.isRecyclingTableUpdated.asObservable();

  constructor(
    private af: AngularFirestore,
    private productService: ProductsService,
    private brandsService: BrandService,
    private userService: UserProviderService,
    private saleService: SaleService,
    private centerService: CenterService,
    private standService: StandService,
    private consumerService: ConsumerService,
    private snackBar: MatSnackBar,
    private translateService: TranslateService
    ) {}

  getPointsAvailable(consumerId: number, brandId: number): Observable<number> {
    return this.af
      .collection<ConsumerPoints>(
        ConsumerFidelityService.collectionPoints,
        query => query
          .where('ID_CONSUMIDORA', '==', consumerId)
          .where('ID_MARCA', '==', brandId)
          .orderBy('FECHA_ULTIMA_ACTUALIZACION', 'desc')
          .limit(1)
      )
      .valueChanges()
      .pipe(
        map(({ 0: consumerPoints }) =>
          consumerPoints?.TOTAL_PUNTOS ? consumerPoints.TOTAL_PUNTOS : 0
        )
      );
  }

  getPointsRedeemed(consumerId: number, brandId: number): Observable<number> {
    return this.af
      .collection<ItemsExchanged>(
        ConsumerFidelityService.collectionPointsRedeemed,
        query => query.where('ID_CONSUMIDORA', '==', consumerId)
      )
      .valueChanges()
      .pipe(
        switchMap(itemsExchanged =>
          zip(
            ...itemsExchanged.map(itemExchanged =>
              from(this.productService.getProductRefByEan(itemExchanged.CODIGO_EAN)
              .get()
              .then(snapshot => snapshot.data()))
                .pipe(
                  map(product => product.ID_MARCA === brandId ? itemExchanged : null)
                )
            )
          )
        ),
        map(itemsExchanged =>
          itemsExchanged
            .filter(itemExchanged => !!itemExchanged)
            .reduce((acc, curr) => acc + curr.VALOR_PUNTOS, 0)
        )
      );
  }

  getPointsPendingExpiration(consumerId: number, brandId: number): Observable<HistoryPointsCalculation> {
    return this.af
      .collection<HistoryPointsCalculation>(
        ConsumerFidelityService.collectionPointsPendingExpiration,
        query => query
          .where('ID_CONSUMIDORA', '==', consumerId)
          .where('ID_MARCA', '==', brandId)
          .orderBy('FECHA_CADUCIDAD')
          .startAt(new Date())
          .limit(1)
      )
      .valueChanges()
      .pipe(
        map(({ 0: historyPointsCalculation }) => historyPointsCalculation ? ({
          ...historyPointsCalculation,
          FECHA_CADUCIDAD: (historyPointsCalculation.FECHA_CADUCIDAD as unknown as firebase.firestore.Timestamp)?.toDate(),
          FECHA_PUNTOS: (historyPointsCalculation.FECHA_PUNTOS as unknown as firebase.firestore.Timestamp)?.toDate()
        }) : undefined)
      );
  }

  getPointsExpired(consumerId: number, brandId: number): Observable<number> {
    return this.af
      .collection<HistoryPoints>(
        ConsumerFidelityService.collectionPointsHistory,
        query => query
          .where('ID_CONSUMIDORA', '==', consumerId)
          .where('ID_MARCA', '==', brandId)
          .where('ID_PP', '==', ConsumerFidelityService.idPromotionExpired)
      )
      .valueChanges()
      .pipe(
        map(historyPoints =>
          historyPoints.filter(a => {
            let fechaPuntos = (a.FECHA_PUNTOS as unknown as firebase.firestore.Timestamp).toDate();
            const dateMinus6Month = (new Date()).setMonth((new Date()).getMonth() - 6);

            // console.log('fechaPuntos ===> ', formatDate(fechaPuntos, 'yyyy-MM-dd', 'en-US') );
            // @ts-ignore
            // eslint-disable-next-line max-len
            return fechaPuntos >= dateMinus6Month;
          }).reduce(
            (historyPointsAcc, historyPointsVal) =>
              historyPointsAcc + historyPointsVal.PUNTOS_OTORGADOS,
            0
          )
        )
      );
  }

  getPointsPerMonth(consumerId: number, brandId: number, startDate: Date, endDate: Date): Observable<HistoryPointsPerMonth[]> {
    const months = eachMonthOfInterval({
      start: startDate,
      end: endDate
    });
    return this.af
      .collection<HistoryPoints>(
        ConsumerFidelityService.collectionPointsHistory,
        query =>
          query
            .where('ID_CONSUMIDORA', '==', consumerId)
            .where('ID_MARCA', '==', brandId)
            .where('ID_PP', '!=', ConsumerFidelityService.idPromotionExpired)
      )
      .valueChanges()
      .pipe(
        map(historyPoints =>
          historyPoints.map(history => ({
            ...history,
            FECHA_PUNTOS: ((history.FECHA_PUNTOS as unknown) as firebase.firestore.Timestamp).toDate()
          }))
        ),
        switchMap(historyPoints =>
          zip(
            ...months.map(month => {
              const monthPoints = historyPoints.filter(
                historyPoint =>
                  historyPoint.FECHA_PUNTOS >= month &&
                  historyPoint.FECHA_PUNTOS <= endOfMonth(month)
              ).reduce((acc, curr) => acc + curr.PUNTOS_OTORGADOS, 0);
              return this.getPointsProductsPerMonth(consumerId, brandId, month).pipe(
                map(productPoints => ({
                  name: month,
                  value: monthPoints,
                  extra: productPoints
                }))
              );
            })
          )
        )
      );
  }

  getPointsListPerDate(consumerId: number, brandId: number, startDate: Date, endDate: Date): Observable<HistoryPointListPerDate[]> {
    return this.af
      .collection<HistoryPoints>(
        ConsumerFidelityService.collectionPointsHistory,
        query => query
          .where('ID_CONSUMIDORA', '==', consumerId)
          .where('ID_MARCA', '==', brandId)
          .where('FECHA_PUNTOS', '>=', startOfDay(startDate))
          .where('FECHA_PUNTOS', '<=', endOfDay(endDate))
      )
      .valueChanges()
      .pipe(
        map(historyPoints =>
          historyPoints
            .map(history => ({
              ...history,
              FECHA_PUNTOS: ((history.FECHA_PUNTOS as unknown) as firebase.firestore.Timestamp).toDate()
            }))
            //.filter(history => history.ID_PP !== ConsumerFidelityService.idPromotionExpired)
        ),
        switchMap(historyPoints =>
          zip(
            ...historyPoints.map(historyPoint =>
              this.getPointsPromoById(historyPoint.ID_PP).pipe(
                switchMap(promoPoint =>
                  this.brandsService.getBrandById(historyPoint.ID_MARCA).pipe(
                    map(brand => {
                      const expireDate = addMonths(historyPoint.FECHA_PUNTOS, brand.MESES_CADUCIDAD_PUNTOS);
                      const expired = isSameMonth(new Date(), expireDate);
                      return {
                        promotion: promoPoint.DESCRIPCION_PP,
                        points: historyPoint.PUNTOS_OTORGADOS,
                        dateUntil: expireDate,
                        nextToExpire: expired
                      };
                    })
                  ))
              ))
          ))
      );
  }

  getPointsPromoById(id: number): Observable<PointsPromo> {
    return this.af
      .doc<PointsPromo>(`${ConsumerFidelityService.collectionPointsPromo}/${id}`)
      .valueChanges();
  }

  getPointsPromosMgm(brandId): Observable<PointsPromo[]> {
    const brandQueryMap = {
      1: 771,   // Lancome
      7: 772,   // Biotherm
      16: 773,  // Armani
      51: 774,  // YSL
      82: 870   // Prada
    };

    const queryId = brandQueryMap[brandId];

    if (queryId) {
      return this.af.collection<PointsPromo>(
        ConsumerFidelityService.collectionPointsPromo,
        query => query
          .where('ID_PP', '==', queryId)
          .limit(1)
      ).get().pipe(
        map(snapshot => snapshot.docs.map(d => d.data())),
        catchError(error => {
          console.log(error);
          return [];
        })
      );
    } else {
      return new Observable();
    }
  }

  getPointsProductsPerMonth(consumerId: number, brandId: number, date: Date): Observable<ProductPoints[]> {
    return this.af
      .collection<ItemsExchanged>(
        ConsumerFidelityService.collectionPointsRedeemed,
        query =>
          query
            .where('ID_CONSUMIDORA', '==', consumerId)
            .where('FECHA_ENTREGADO_AC', '>=', startOfMonth(date))
            .where('FECHA_ENTREGADO_AC', '<=', endOfMonth(date))
      )
      .valueChanges()
      .pipe(
        switchMap(itemsExchanged =>
          zip(
            ...itemsExchanged.map(itemExchanged =>
              this.productService
                .getByEAN(itemExchanged.CODIGO_EAN)
                .pipe(map(product => ({ product, itemExchanged })))
            )
          ).pipe(
              map(productPoints =>
                productPoints
                  .filter(({ product }) => product.ID_MARCA === brandId)
                  .map(({ product, itemExchanged }) => ({
                    name: product.DESCRIPCION_ARTICULO,
                    EAN: product.CODIGO_EAN,
                    points: itemExchanged.VALOR_PUNTOS
                  }))
              ),
              defaultIfEmpty<ProductPoints[]>([])
          )
        )
      );
  }

  getPointBrandByConsumer(consumerId: number): Observable<BrandsPoints[]> {
    return this.userService.getDefaultBrands().pipe(
      switchMap(brands =>
        zip(
          ...brands.map(brand =>
            this.getPointsAvailable(consumerId, brand.ID_MARCA).pipe(
              map(point => ({
                brand: brand.NOMBRE_MARCA,
                points: point
              }))
            )
          )
        ).pipe(
          map(brandsMap => brandsMap.filter(brand => brand.points))
        )
      )
    );
  }



  getPointBrandByConsumerAllBrands(consumerId: number): Observable<BrandsPoints[]> {
    return this.brandsService.getAllBrands().pipe(
      switchMap(brands =>
        zip(
          ...brands.map(brand =>
            this.getPointsAvailable(consumerId, brand.ID_MARCA).pipe(
              map(point => ({
                brand: brand.NOMBRE_MARCA,
                points: point
              }))
            )
          )
        ).pipe(
          map(brandsMap => brandsMap.filter(brand => brand.points))
        )
      )
    );
  }

  getLastDateSale(consumerId: number): Observable<Date> {
    return this.af
    .collection<Sale>(
      ConsumerFidelityService.collectionSales,
      query =>
        query
          .where('ID_CONSUMIDORA', '==', consumerId)
          .orderBy('FECHA_VENTA', 'desc')
          .limit(1)
    )
    .valueChanges()
    .pipe(
      map(({ 0: sale }) => ((sale?.FECHA_VENTA as unknown) as firebase.firestore.Timestamp)?.toDate()),
      defaultIfEmpty()
    );
  }

  getLastSales(consumerId: number): Observable<Sale[]> {
    return this.af
    .collection<Sale>(
      ConsumerFidelityService.collectionSales,
      query =>
        query
          .where('ID_CONSUMIDORA', '==', consumerId)
          .orderBy('FECHA_VENTA', 'desc')
          .limit(5)
    )
    .valueChanges()
    .pipe(
      map(sales => sales.map(sale => ({
        ...sale,
        FECHA_VENTA: ((sale?.FECHA_VENTA as unknown) as firebase.firestore.Timestamp)?.toDate()
      }))),
      defaultIfEmpty<Sale[]>([])
    );
  }

  getIsConsumerPromoFidelity(consumerId: number, brandId: number, totalPriceByBrand?: number): Observable<number> {
    return this.af
    .collection<ConsumerBrand>(
      ConsumerFidelityService.collectionConsumerBrand,
      query =>
        query
          .where('ID_CONSUMIDORA', '==', consumerId)
          .where('ID_MARCA', '==', brandId)
          .limit(1)
    )
    .valueChanges()
    .pipe(
      map(({ 0: consumerBrands }) => {
        return consumerBrands?.PROGRAMA_FIDELIDAD;
      }),
      switchMap((isFidelity) => {
        if (isFidelity === 1) {
          return of(1);
        } else {
          return this.recalculateSalesAndCheckFidelity(consumerId, brandId, totalPriceByBrand);
        }
      }),
      defaultIfEmpty<number>(0)
    );
  }

  /**
   * Given a consumerId and a brandId, it gets the amount necessary to belong to its fidelity program,
   * and if the sales in the last X months (including current sale, that total comes from totalPriceBrand) surpasses the threshold
   * in DINERO_FIDELIDAD it will add the consumer to the fidelity program and return 1 (to continue as if the consumer was already
   * in the fidelity program).
   *
   * If it cannot add the consumer to the fidelity program or the total amount spend by the consumer doesn't
   * reach the threshold, it will return 0 (to continue as if the consumer wasn't in the fidelity program)
   */
  recalculateSalesAndCheckFidelity(consumerId: Consumer['ID_CONSUMIDORA'], brandId: Brand['ID_MARCA'], totalPriceByBrand: number) {
    return this.brandsService.getBrandById(brandId).pipe(
      take(1),
      map((brand) => brand.DINERO_FIDELIDAD),
      switchMap((DINERO_FIDELIDAD) => {
        return this.getTotalSalesForConsumerInBrand(consumerId, brandId).pipe(
          switchMap((totalSalesAmount) => (totalSalesAmount + totalPriceByBrand) >= DINERO_FIDELIDAD ?
            of(1) :
            of(0)
          )
        );
      }),
      catchError((err) => {
        console.error(err);
        return of(0);
      }),
    );
  }

  /**
   * This method returns the total amount spend by a consumer in a brand in the last X months.
   * X is set in ConsumerFidelityService.howManyMonthsOfSalesForFidelity
   */
  private getTotalSalesForConsumerInBrand(consumerId: number, brandId: number): Observable<number> {
    return this.saleService.getAllSalesInAPeriodOfTime(
      consumerId,
      subMonths(new Date(), ConsumerFidelityService.howManyMonthsOfSalesForFidelity),
      new Date(),
      brandId
    )
      .get().pipe(
        map((snapshot) => snapshot.docs.map((singleSnapshot) => singleSnapshot.data())),
        defaultIfEmpty([] as SaleItem[]),
        map(salesArr => salesArr.reduce<number>((prev, curr) => (prev + curr.TOTAL_ARTICULOS), 0)),
      );
  }

  getAnnualKpis(consumerId: number, brandId?: number): Observable<KpisAnnual> {
    return this.af
      .collection<SaleItem>(
        ConsumerFidelityService.collectionProductSale,
        query =>{
          let _query = query
          .where('ID_CONSUMIDORA', '==', consumerId)
          .where('FECHA_ARTICULOS_VENTA', '>=', startOfYear(new Date()))
          if (brandId) {
           _query = _query.where('ID_MARCA', '==', brandId);
          }
          return _query;
        })
      .valueChanges()
      .pipe(
        switchMap(productSale =>
          zip(
            ...productSale.map(product =>
              this.productService
                .getByEAN(product.CODIGO_EAN, null, false, false, true)
                .pipe(
                  map(prod => ({
                    ...product,
                    prod
                  }))
                )
            )
          ).pipe(defaultIfEmpty([]))
        ),
        map(productSale => {
          const sum = productSale.reduce((acc, curr) => {
            if (
              !(
                curr.prod.TIPO_ARTICULO === ProductTypes.MU ||
                curr.prod.TIPO_ARTICULO === ProductTypes.AC ||
                curr.prod.TIPO_ARTICULO === ProductTypes.PR ||
                curr.prod.TIPO_ARTICULO === ProductTypes.RE
              )
            ) {
              return acc + curr.TOTAL_ARTICULOS;
            }
            return acc;
          }, 0);
          const groupByOrder = productSale.reduce((total, value) => {
            if (
              !(
                value.prod.TIPO_ARTICULO === ProductTypes.MU ||
                value.prod.TIPO_ARTICULO === ProductTypes.AC ||
                value.prod.TIPO_ARTICULO === ProductTypes.PR ||
                value.prod.TIPO_ARTICULO === ProductTypes.RE
              )
            ) {
              return {
                ...total,
                [value.ID_ORDEN_VENTA]: (total[value.ID_ORDEN_VENTA] || 0) + 1
              };
            }
            return total;
          }, {} as { [prop: string]: number });
          const count = Object.keys(groupByOrder).length;
          const sumOrder = Object.values(groupByOrder).reduce(
            (totalProductCount, saleProductCount) =>
              Number(totalProductCount) + Number(saleProductCount),
            0
          );
          if (sum && count) {
            return {
              expense: sum,
              annualVisit: count,
              avarage: sum / count,
              avarageItems: Number(sumOrder) / count
            };
          } else {
            return {
              annualVisit: 0,
              avarage: 0,
              avarageItems: 0,
              expense: 0
            };
          }
        })
      );
  }

  addExchangedItemsPoints({
    amountOfPoints,
    consumerId,
    ean,
    standId,
    userId,
    requestedDate = new Date(),
    listedDate = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate(), 0, 0, 0),
    receivedDate = null,
    deliveredDate = null,
    deliveryUser = null,
    receivedUser = null,
    standIdDelivered = null,
  }: {
    amountOfPoints: number,
    consumerId: number,
    brandId: number,
    ean: string,
    standId: number,
    userId: number,
    requestedDate?: Date,
    listedDate?: Date,
    receivedDate?: Date,
    deliveredDate?: Date,
    deliveryUser?: number,
    receivedUser?: number,
    standIdDelivered?: number,
  }) {
    const id = this.af.createId();
    const payload = {
      ID_USUARIO: userId,
      ID_STAND: standId,
      CODIGO_EAN: ean,
      FECHA_PEDIDO_AC: requestedDate,
      FECHA_LISTA_AC: listedDate,
      FECHA_RECIBIDO_AC: receivedDate,
      FECHA_ENTREGADO_AC: deliveredDate,
      USUARIO_ENTREGA: deliveryUser,
      USUARIO_RECIBIDO: receivedUser,
      ID_STAND_ENTREGADO: standIdDelivered,
      VALOR_PUNTOS: amountOfPoints,
      ID_CONSUMIDORA: consumerId,
      ID_ARTICULO_CANJE: id,
      FechaDevolucion: null,
    };
    return this.af.doc(`${ConsumerFidelityService.collectionPointsRedeemed}/${id}`).set(payload);
  }

  public addConsumerPoints(
    points: number,
    idConsumer: number,
    idSale: number | string,
    idPromotion: number,
    idUser: number,
    idBrand: number,
    idStand?: number,
    date: Date = new Date()
  ): Promise<void> {
    const id = this.af.createId();
    return this.af.doc<HistoryPoints>(`${ConsumerFidelityService.collectionPointsHistory}/${id}`).set({
      FECHA_PUNTOS: date,
      ID_CONSUMIDORA: idConsumer,
      ID_HISTORICO_PUNTOS: id,
      ID_MARCA: idBrand,
      ID_ORDEN_VENTA: idSale,
      ID_PP: idPromotion,
      ID_STAND: idStand ?? null,
      ID_USUARIO: idUser,
      PUNTOS_OTORGADOS: points,
      STOCK: null
    });
  }

  public async addToConsumerBrandRecycling(userLogged:UserModel, brandDefault:Brand, date: Date, idConsumer: number, bottles:number) {
    try {
      const id = this.af.createId();
      //ID de la promoción o ID_PP = 741
      const promo = await this.getPointsPromoById(741).pipe(take(1)).toPromise();
      
      const idSale = this.af.createId();

      this.addConsumerPoints(
        bottles * promo.PUNTOS_EXTRA, idConsumer, idSale, promo.ID_PP, Number(userLogged.id), brandDefault.ID_MARCA);

      const historicPointsId = await this.getHistoricPointsId(idSale, promo.ID_PP);

      const centerName = JSON.parse(localStorage.getItem('userDiv'))['centro'];
      const standName = JSON.parse(localStorage.getItem('userDiv'))['stand'];

      const center = await this.centerService.getCenterByName(centerName);
      const stands = await this.standService.getStandsByCenter(center[0]).pipe(take(1)).toPromise();
      const stand = stands.filter(stand => stand.DESCRIPCION_STAND === standName);

      await this.af
        .collection(`CONSUMIDORA_MARCA_RECICLAJE`)
        .doc(`${id}`).set({
          FECHA: date,
          ID_CONSUMIDORA: idConsumer,
          ID_MARCA: brandDefault.ID_MARCA,
          ID_PP: promo.ID_PP,
          ID_HISTORICO_PUNTOS: historicPointsId,
          ID_STAND: stand[0].ID_STAND,
          ID_USUARIO: Number(userLogged.id),
          NUM_BOTELLAS: bottles,
          PUNTOS: bottles * promo.PUNTOS_EXTRA,
        });

      this.changeRecyclingTableState(true);
      
      this.snackBar.open(
        this.translateService.instant('Consumers.Loyalty.RecyclingSuccess'),
        'OK',
        { duration: 4000 }
      );
    } catch (error) {
      console.error('Error al registrar botellas recicladas:', error);
      this.snackBar.open(
        this.translateService.instant('Consumers.Loyalty.RecyclingError'),
        'OK',
        { duration: 5000 }
      );
    }
  }

  private async getHistoricPointsId(idSale, idPP){
    const snapshot = await this.af.collection<HistoryPoints>(ConsumerFidelityService.collectionPointsHistory, (query: Query) => {
      query = query
        .where('ID_ORDEN_VENTA', '==', idSale)
        .where('ID_PP', '==', idPP);

      return query;
    }).get().toPromise();

    const data = snapshot.docs.map(doc => doc.data());
    const historicPointsId = data[0].ID_HISTORICO_PUNTOS;

    return historicPointsId;
  }

  changeRecyclingTableState(state:boolean) {
    this.isRecyclingTableUpdated.next(state);
  }

  /**
   * @description Method to update EstadoPedido status in ARTICULOS_CANJE
   */
  public updateExchangedItemsOrder(row: Partial<RedeemableArticles>): Promise<void> {
    return this.af.doc<RedeemableArticles>(`${ConsumerFidelityService.collectionPointsRedeemed}/${row.ID_ARTICULO_CANJE}`).update(row);
  }

  /**
   * @description Method to update ARTICULOS_CANJE when a consumer signs that a product has been delivered.1º
   */
  updateDeliveredStatus(
    exchangedItemCode: number,
    newDeliveredDate: Date,
    userId: any,
    idStand: number,
    requestStatus: ExchangedItemsStatusCode
  ) {
    return this.af.doc(`${ConsumerFidelityService.collectionPointsRedeemed}/${exchangedItemCode}`)
      .update({
        FECHA_ENTREGADO_AC: newDeliveredDate,
        ID_STAND_ENTREGADO: idStand,
        USUARIO_ENTREGA: userId,
        EstadoPedido: requestStatus
      });
  }

  /**
   * @description Method to update ARTICULOS_CANJE.FECHA_LISTA_AC to next cycle.
   */
  updateListedDateToNextCycle(row: RedeemableArticles) {
    const baseDate = row.FECHA_LISTA_AC ?? new Date();
    // const next01 = addMonths(set(baseDate, {
    //   date: 1,
    // }), 1);
    // let next15: Date;
    // if (getDate(baseDate) >= 15) {
    //   next15 = addMonths(set(baseDate, {
    //     date: 15,
    //   }), 1);
    // } else {
    //   next15 = set(baseDate, {
    //     date: 15,
    //   });
    // }
    return this.af.doc(`${ConsumerFidelityService.collectionPointsRedeemed}/${row.ID_ARTICULO_CANJE}`).update({
      EstadoPedido: ExchangedItemsStatusCode.NOSTOCKBUTCONTACTED//,
      //FECHA_LISTA_AC: closestTo(new Date(), [next01, next15])
    });
  }
}
